import { IPStack } from 'baseComponents/Stack/IPStack';
import { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { IAIFeedback } from 'components/AI/AIFeedback';
import { globalStackTokensGapMedium } from 'globalStyles';
import {
  CopilotConversation,
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
  CopilotLibraryDocumentEditorRequest,
} from 'models/copilot/copilotConversation';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CopilotAgentEvent } from 'services/SignalR/CopilotHub';
import { hasChatItems } from '../Runtime/copilotConversationHelpers';
import { CopilotAgentEventList } from './CopilotAgentEventList';
import { CopilotChatItem } from './CopilotChatItem';

const emptyChatItems: CopilotConversationItem[] = [];

interface ICopilotChatProps {
  conversation?: CopilotConversation;
  // the Copilot session of the conversation, for the download of a File block
  sessionId?: number;
  isStreaming?: boolean;
  streamText?: string;
  streamStatus?: string;
  agentEvents?: CopilotAgentEvent[];
  welcomeMessage?: string;
  onEntitySelection: (selection: number[]) => void;
  onAdaptiveInputsChanged?: (
    itemId: string | undefined,
    blockId: string | undefined,
    inputs: Record<string, string>,
  ) => void;
  onAdaptiveSubmit?: (
    itemId: string | undefined,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
  ) => void;
  onApprovalDetails?: (
    itemId: string | undefined,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
  ) => void;
  onOpenLibraryDocumentEditor?: (request: CopilotLibraryDocumentEditorRequest) => void | Promise<void>;
  onEditLastQuestion?: (itemId: string | undefined, question: string) => void | Promise<void>;
  /** The verdict on one answer in the thread. Absent leaves the thumbs out of every bubble. */
  onFeedback?: (feedback: IAIFeedback) => void;
  /** True while the annual plan is the active usage: its older selection items carry no question. */
  isAnnualPlan?: boolean;
}

export const CopilotChat = (props: ICopilotChatProps) => {
  const { t } = useTranslation(['copilot']);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const chatItems = props.conversation?.thread ?? emptyChatItems;
  const agentEventCount = props.agentEvents?.length ?? 0;
  const welcomeItem = useMemo(() => {
    if (!props.welcomeMessage) return undefined;

    const item = new CopilotConversationItem();
    item.type = CopilotConversionItemType.Copilot;
    item.response = props.welcomeMessage;

    return item;
  }, [props.welcomeMessage]);
  const visibleItems = useMemo(() => {
    if (!hasChatItems(props.conversation) && welcomeItem) return [welcomeItem];

    return chatItems;
  }, [chatItems, props.conversation, welcomeItem]);
  const streamingChatItem = useMemo(() => {
    if (!props.isStreaming || (!props.streamText && !props.streamStatus)) return undefined;

    const item = new CopilotConversationItem();
    item.type = CopilotConversionItemType.Copilot;
    item.date = new Date();
    item.responseRaw = props.streamText ?? '';
    item.response = item.responseRaw;
    item.copilotStatus = props.streamStatus;

    return item;
  }, [props.isStreaming, props.streamText, props.streamStatus]);
  // The request each turn answers: its date goes to the turn's last answer item ("Thought for") and to the
  // streaming item (the Thinking time).
  const requestDates = useMemo(() => {
    const dates = new Map<CopilotConversationItem, Date>();
    let requestDate: Date | undefined;
    visibleItems.forEach((item, index) => {
      if (item.type === CopilotConversionItemType.User) {
        requestDate = item.date;

        return;
      }

      const next = visibleItems[index + 1];
      const isLastOfTurn = !next || next.type === CopilotConversionItemType.User;
      if (requestDate && isLastOfTurn && item.type === CopilotConversionItemType.Copilot) {
        dates.set(item, requestDate);
      }
    });

    return dates;
  }, [visibleItems]);
  const lastRequestDate = useMemo(
    () => [...visibleItems].reverse().find((item) => item.type === CopilotConversionItemType.User)?.date,
    [visibleItems],
  );
  const editableQuestionItemId = useMemo(() => {
    if (props.isStreaming || !props.onEditLastQuestion) return undefined;

    const lastIndex = visibleItems.length - 1;
    const lastItem = visibleItems[lastIndex];
    if (
      lastItem?.type === CopilotConversionItemType.User &&
      lastItem.question &&
      lastItem.selectedEntityIds === undefined
    ) {
      return lastItem.id;
    }

    const previousItem = visibleItems[lastIndex - 1];
    if (
      lastItem?.type === CopilotConversionItemType.Copilot &&
      lastItem.response === t('copilot:Chat.QuestionCancelled') &&
      previousItem?.type === CopilotConversionItemType.User &&
      previousItem.question &&
      previousItem.selectedEntityIds === undefined
    ) {
      return previousItem.id;
    }

    return undefined;
  }, [props.isStreaming, props.onEditLastQuestion, t, visibleItems]);

  //
  // Effects
  //
  useEffect(() => {
    if (streamingChatItem || props.streamStatus || visibleItems.length > 0) {
      messagesEndRef.current?.scrollIntoView(false);
    }
  }, [streamingChatItem, visibleItems.length, props.streamStatus, agentEventCount]);

  //
  // Main render
  //
  return (
    <IPStack
      verticalFill
      tokens={globalStackTokensGapMedium}
      styles={{
        root: { height: '100%', minHeight: 0, overflowX: 'hidden', overflowY: 'auto', paddingRight: 10, width: '100%' },
      }}
    >
      {visibleItems.map((item) => (
        <CopilotChatItem
          key={item.id}
          item={item}
          sessionId={props.sessionId}
          onEntitySelection={props.onEntitySelection}
          onAdaptiveInputsChanged={(blockId, inputs) => props.onAdaptiveInputsChanged?.(item.id, blockId, inputs)}
          onAdaptiveSubmit={(block, payload) => props.onAdaptiveSubmit?.(item.id, block, payload)}
          onApprovalDetails={(block, payload) => props.onApprovalDetails?.(item.id, block, payload)}
          onOpenLibraryDocumentEditor={props.onOpenLibraryDocumentEditor}
          isMostRecent={item === visibleItems[visibleItems.length - 1] && !props.isStreaming}
          pendingQuestionBlockId={props.isStreaming ? undefined : props.conversation?.pendingQuestionBlockId}
          canEditLastQuestion={item.id === editableQuestionItemId}
          onEditLastQuestion={(question) => props.onEditLastQuestion?.(item.id, question)}
          onFeedback={props.onFeedback}
          canGiveFeedback={item !== welcomeItem}
          isAnnualPlan={props.isAnnualPlan}
          requestDate={
            props.isStreaming && item === visibleItems[visibleItems.length - 1] ? undefined : requestDates.get(item)
          }
        />
      ))}
      {streamingChatItem && (
        <CopilotChatItem
          key="streaming-response"
          item={streamingChatItem}
          isStreaming={true}
          onEntitySelection={props.onEntitySelection}
          isAnnualPlan={props.isAnnualPlan}
          requestDate={lastRequestDate}
        />
      )}
      <CopilotAgentEventList events={props.isStreaming ? props.agentEvents ?? [] : []} />
      <div ref={messagesEndRef} />
    </IPStack>
  );
};
